import { useEffect, useMemo, useRef, useState } from 'react';
import SvgViewer from './components/SvgViewer';
import {
    getAutoSeating,
    useGetTicketSeats,
    useGetFreeTicketSeats,
} from '../openapi/ticket-seats/ticket-seats';
import { useGetTicketTypes } from '../openapi/ticket-types/ticket-types';
import { useGetShow, useGetArrangementPositions } from '../openapi/shows/shows';
import { useGetTimeslots } from '../openapi/timeslots/timeslots';
import { calculateColorMap } from './helpers/colorMap';
import { applyArrangementOverlay, buildPositionMap } from './helpers/arrangementOverlay';
import {
    GetAutoSeating200SuggestionsItem,
    TicketSeatsSuccessResponseResponseTicketSeatsItem,
    TicketTypesSuccessResponseResponseTicketTypesItem,
} from '../openapi/model';
import { II18nLanguageObj, useLang } from './hooks/useLanguage';
import Chip from './components/Chip';
import { useShowStyling } from './helpers/showStyling';

// ---- postMessage types (aligned with parent SeatingIframe) ----

const MSG = {
    // iframe → parent
    READY: 'IFRAME_READY',
    SEAT_SELECTED: 'SEAT_SELECTED',
    SEAT_DESELECTED: 'SEAT_DESELECTED',
    SEATS_UPDATED: 'SEATS_UPDATED',
    SEATS_CONFIRMED: 'SEATS_CONFIRMED',
    // parent → iframe
    INIT: 'SEATING_INIT',
    SELECT_SEATS: 'SELECT_SEATS',
} as const;

// ---- Types ----

interface IframeSeat {
    id: number;
    name: string;
    nameI18n: II18nLanguageObj;
    ticketTypeId: number;
    ticketTypeName: string;
    ticketTypeNameI18n: II18nLanguageObj;
}

// Parent expects seat data in this format
interface ExternalSeat {
    seatId: string;
    label: string;
    type: string;
}

interface IframeCartItem {
    ticketTypeId: number;
    ticketOptionId: number;
    quantity: number;
}

interface IframeElement {
    id: number;
    name: string;
    nameI18n: II18nLanguageObj;
    x: number;
    y: number;
    rotation: number;
    scale: number;
    svg: string;
    model?: string;
    bookable: boolean;
}

// ---- URL Param Parsing ----

export function parseIframeParams(): {
    showId: number;
    timeslotId: number;
    cart: IframeCartItem[];
} | null {
    const params = new URLSearchParams(window.location.search);
    const showId = Number(params.get('showId'));
    // Support both timeSlotId and timeslotId
    const timeslotId = Number(params.get('timeSlotId') || params.get('timeslotId'));
    if (!showId || !timeslotId) return null;

    let cart: IframeCartItem[] = [];
    try {
        // Tickets as plain JSON string
        const ticketsParam = params.get('tickets');
        if (ticketsParam) cart = JSON.parse(ticketsParam);
    } catch {
        /* empty */
    }
    // Fallback: support base64-encoded cart param for backward compat
    if (cart.length === 0) {
        try {
            const cartParam = params.get('cart');
            if (cartParam) cart = JSON.parse(atob(cartParam));
        } catch {
            /* empty */
        }
    }

    return { showId, timeslotId, cart };
}

// ---- Helpers ----

function mapFromDbToElement(
    seat: TicketSeatsSuccessResponseResponseTicketSeatsItem,
): IframeElement {
    let svgInfo = seat?.svgInfo;
    if (svgInfo === null || svgInfo === undefined) {
        svgInfo = {};
    }
    return {
        id: seat.id,
        name: seat.name,
        nameI18n: seat.nameI18n,
        x: svgInfo.x || 0,
        y: svgInfo.y || 0,
        rotation: svgInfo.rotation || 0,
        scale: svgInfo.scale || 1,
        model: svgInfo.model || '',
        svg: svgInfo.svg || '',
        bookable: seat.bookable,
    };
}

function getSeatNeeds(
    cart: IframeCartItem[],
    ticketTypes: TicketTypesSuccessResponseResponseTicketTypesItem[] | undefined,
): Record<number, number> {
    if (!ticketTypes) return {};
    const grouped: Record<number, number> = {};
    cart.forEach((item) => {
        const ticketType = ticketTypes.find((type) => type.id === item.ticketTypeId);
        if (!ticketType || ticketType.seats?.length === 0) return;
        if (!grouped[item.ticketTypeId]) grouped[item.ticketTypeId] = 0;
        grouped[item.ticketTypeId] += item.quantity;
    });
    return grouped;
}

function toExternalSeat(seat: IframeSeat): ExternalSeat {
    return {
        seatId: String(seat.id),
        label: seat.name,
        type: seat.ticketTypeName,
    };
}

// ---- Parent origin for postMessage ----

function getParentOrigin(): string {
    try {
        if (document.referrer) {
            return new URL(document.referrer).origin;
        }
    } catch {
        /* empty */
    }
    return '*';
}

const parentOrigin = getParentOrigin();

function postToParent(message: Record<string, unknown>) {
    window.parent.postMessage(message, parentOrigin);
}

// ---- Component ----

export default function SeatPickerIframe({
    showId,
    timeslotId,
    initialCart,
}: {
    showId: number;
    timeslotId: number;
    initialCart: IframeCartItem[];
}) {
    const [seats, setSeatsRaw] = useState<IframeSeat[]>([]);
    const [maxSeats, setMaxSeats] = useState<number>(Infinity);
    const [elements, setElements] = useState<IframeElement[]>([]);
    const [showLabel, setShowLabel] = useState(false);
    const [panelOpen, setPanelOpen] = useState(true);
    const readyFiredRef = useRef(false);
    const { getI18n, t } = useLang();

    const { data: show } = useGetShow(showId);
    useShowStyling(show);
    const delayedSeatAssignment = !!show?.marketing?.delayedSeatAssignmentEnabled;
    const shouldLoadSeatData = !!show && !delayedSeatAssignment;
    const { data: { ticketTypes } = { ticketTypes: [] } } = useGetTicketTypes(
        {
            optionalId: showId,
            includeSeats: true,
            onlyShowInList: true,
            onlyGuestVisible: true,
        },
        {
            query: { enabled: shouldLoadSeatData },
        },
    );
    const { data: { ticketSeats: allSeats } = { ticketSeats: [] } } = useGetTicketSeats(
        showId,
        {
            pageSize: 1000,
            page: 0,
        },
        {
            query: { enabled: shouldLoadSeatData },
        },
    );
    const { data: { ticketSeats: freeSeats } = { ticketSeats: [] } } = useGetFreeTicketSeats(
        showId,
        { pageSize: 1000, page: 0, timeslotId },
        {
            query: { enabled: shouldLoadSeatData },
        },
    );

    // Resolve the timeslot's effective arrangement (its own override, else the
    // show default). getTimeslot is not anonymous, so fetch just this timeslot
    // via the anonymous getTimeslots idList filter.
    const { data: timeslotsData } = useGetTimeslots(
        { idList: [timeslotId] },
        { query: { enabled: !!timeslotId } },
    );
    const timeslotArrangementId =
        (
            timeslotsData?.timeslots?.find((ts) => Number(ts.id) === timeslotId) as
                | { activeArrangementId?: number | null }
                | undefined
        )?.activeArrangementId ?? null;
    const activeArrangementId =
        timeslotArrangementId ?? (show?.activeArrangementId as number | null | undefined) ?? null;
    // The timeslot's arrangement isn't known until its row loads; until then we
    // can't tell which seats are excluded, so gate rendering instead of briefly
    // showing the show-default (or no) overlay.
    const timeslotPending = !!timeslotId && !timeslotsData;
    const { data: positionsData } = useGetArrangementPositions(showId, activeArrangementId || 0, {
        query: { enabled: !!showId && !!activeArrangementId },
    });

    const { displayElements, arrangementExcludedIds } = useMemo(() => {
        if (timeslotPending) {
            return {
                displayElements: [] as typeof elements,
                arrangementExcludedIds: new Set<number>(),
            };
        }
        if (!activeArrangementId) {
            return { displayElements: elements, arrangementExcludedIds: new Set<number>() };
        }
        // Gate rendering until arrangement positions load so removed seats are
        // never briefly shown or clickable.
        if (!positionsData?.positions) {
            return {
                displayElements: [] as typeof elements,
                arrangementExcludedIds: new Set<number>(),
            };
        }
        const overlay = applyArrangementOverlay(
            elements,
            buildPositionMap(positionsData.positions),
        );
        return {
            displayElements: overlay.displayElements,
            arrangementExcludedIds: new Set(overlay.excludedSeatIds),
        };
    }, [elements, activeArrangementId, positionsData, timeslotPending]);

    const filteredFreeSeats = useMemo(() => {
        if (!arrangementExcludedIds.size) return freeSeats;
        return freeSeats?.filter((s) => !arrangementExcludedIds.has(Number(s.id)));
    }, [freeSeats, arrangementExcludedIds]);

    const [suggestedSeats, setSuggestedSeats] = useState<GetAutoSeating200SuggestionsItem[]>([]);
    const autoSuggestionsAppliedRef = useRef(false);

    // Send IFRAME_READY on mount (once)
    useEffect(() => {
        if (readyFiredRef.current) return;
        readyFiredRef.current = true;
        postToParent({ type: MSG.READY });
    }, []);

    // Build elements from all seats
    useEffect(() => {
        if (!allSeats) return;
        setElements(allSeats.map(mapFromDbToElement));
    }, [allSeats]);

    // Listen for parent messages (SEATING_INIT, SELECT_SEATS)
    useEffect(() => {
        const resolveSeats = (incoming: { seatId?: string; id?: number }[]): IframeSeat[] => {
            if (!ticketTypes?.length) return [];
            const resolved: IframeSeat[] = [];
            incoming.forEach((s) => {
                const seatId = Number(s.seatId ?? s.id);
                for (const ticketType of ticketTypes) {
                    const seat = ticketType.seats?.find(
                        (ts) => (ts.id as unknown as number) === seatId,
                    );
                    if (seat) {
                        resolved.push({
                            id: seatId,
                            name: seat.name,
                            nameI18n: seat.nameI18n,
                            ticketTypeId: ticketType.id,
                            ticketTypeName: ticketType.name,
                            ticketTypeNameI18n: ticketType.nameI18n,
                        });
                        break;
                    }
                }
            });
            return resolved;
        };

        const handler = (event: MessageEvent) => {
            const { type } = event.data ?? {};
            if (type === MSG.INIT) {
                if (event.data.maxSeats != null) setMaxSeats(event.data.maxSeats);
                if (Array.isArray(event.data.selectedSeats)) {
                    setSeatsRaw(resolveSeats(event.data.selectedSeats));
                }
            }
            if (type === MSG.SELECT_SEATS && Array.isArray(event.data.seats)) {
                setSeatsRaw(resolveSeats(event.data.seats));
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [ticketTypes]);

    const seatNeeds = getSeatNeeds(initialCart, ticketTypes);
    const noSeatingRequired =
        (ticketTypes.length > 0 && Object.keys(seatNeeds).length === 0) || delayedSeatAssignment;

    // When no seat picking is needed, immediately confirm with no seats
    const noSeatingNotifiedRef = useRef(false);
    useEffect(() => {
        if (!noSeatingRequired || noSeatingNotifiedRef.current) return;
        noSeatingNotifiedRef.current = true;
        setSeatsRaw([]);
        postToParent({
            type: MSG.SEATS_CONFIRMED,
            confirmed: true,
            seats: [],
            noSeatingRequired: true,
        });
    }, [noSeatingRequired]);

    // Check if all required seats are filled and notify parent
    const checkAndNotifyConfirmation = (currentSeats: IframeSeat[]) => {
        const allFilled = Object.entries(seatNeeds).every(([ticketTypeId, quantity]) => {
            const selected = currentSeats.filter(
                (s) => s.ticketTypeId === parseInt(ticketTypeId, 10),
            );
            return selected.length >= quantity;
        });
        postToParent({
            type: MSG.SEATS_CONFIRMED,
            confirmed: allFilled,
            seats: currentSeats.map(toExternalSeat),
        });
    };

    // ---- Auto-seating ----

    const autoSeatingRequirements = Object.entries(seatNeeds)
        .map(([ticketTypeId, quantity]) => {
            const ticketType = ticketTypes.find((t) => t.id === parseInt(ticketTypeId, 10));
            return {
                ticketTypeId: parseInt(ticketTypeId, 10),
                quantity,
                automaticSeating: ticketType?.automaticSeating || 'manual',
            };
        })
        .filter((req) => req.automaticSeating !== 'manual');

    // Fetch auto-seating suggestions once data is available
    useEffect(() => {
        if (autoSuggestionsAppliedRef.current) return;
        if (!timeslotId || autoSeatingRequirements.length === 0 || !ticketTypes?.length) return;

        const fetchSuggestions = async () => {
            try {
                const response = await getAutoSeating(timeslotId, {
                    currentSeats: seats.map((e) => ({ id: e.id, ticketTypeId: e.ticketTypeId })),
                    requiredSeats: autoSeatingRequirements.map((req) => ({
                        ticketTypeId: req.ticketTypeId,
                        quantity: req.quantity,
                    })),
                });
                if (response?.suggestions) {
                    setSuggestedSeats(response.suggestions);
                }
            } catch (error) {
                console.error('Auto-seating suggestions error:', error);
            }
        };
        fetchSuggestions();
    }, [timeslotId, autoSeatingRequirements, ticketTypes, activeArrangementId]);

    // Auto-apply fullauto seats only (auto/semiauto are shown as suggestions for user to accept)
    useEffect(() => {
        if (autoSuggestionsAppliedRef.current || !suggestedSeats.length || !ticketTypes?.length)
            return;
        autoSuggestionsAppliedRef.current = true;

        const newSeats: IframeSeat[] = [];
        suggestedSeats.forEach((suggestion) => {
            const ticketType = ticketTypes.find((t) => t.id === suggestion.ticketTypeId);
            if (!ticketType || ticketType.automaticSeating !== 'fullauto') return;

            const neededQuantity = seatNeeds[suggestion.ticketTypeId] || 0;
            const alreadySelectedIds = new Set(
                seats.filter((s) => s.ticketTypeId === suggestion.ticketTypeId).map((s) => s.id),
            );
            suggestion.seats
                ?.filter((s) => !alreadySelectedIds.has(s.id as number))
                .slice(0, Math.max(0, neededQuantity - alreadySelectedIds.size))
                .forEach((seat) => {
                    newSeats.push({
                        id: seat.id as number,
                        name: seat.name as string,
                        nameI18n: (seat.nameI18n || {}) as II18nLanguageObj,
                        ticketTypeId: suggestion.ticketTypeId as number,
                        ticketTypeName: ticketType.name,
                        ticketTypeNameI18n: ticketType.nameI18n,
                    });
                });
        });

        if (newSeats.length > 0) {
            const updatedSeats = [...seats, ...newSeats];
            setSeatsRaw(updatedSeats);
            postToParent({ type: MSG.SEATS_UPDATED, seats: updatedSeats.map(toExternalSeat) });
            checkAndNotifyConfirmation(updatedSeats);
        }
    }, [suggestedSeats, ticketTypes, seatNeeds]);

    // Derived values
    const fullAutoTicketTypeIds = new Set(
        (ticketTypes || []).filter((t) => t.automaticSeating === 'fullauto').map((t) => t.id),
    );

    const hasAutoSuggestions = suggestedSeats.some((suggestion) => {
        const ticketType = ticketTypes?.find((type) => type.id === suggestion.ticketTypeId);
        return ticketType?.automaticSeating === 'auto';
    });

    const hasClearableSeats = seats.some((seat) => !fullAutoTicketTypeIds.has(seat.ticketTypeId));

    // Actions
    function updateSeatsAndNotify(newSeats: IframeSeat[]) {
        setSeatsRaw(newSeats);
        postToParent({ type: MSG.SEATS_UPDATED, seats: newSeats.map(toExternalSeat) });
        checkAndNotifyConfirmation(newSeats);
    }

    function acceptAllSuggestedSeats() {
        const newSeats: IframeSeat[] = [];
        suggestedSeats.forEach((suggestion) => {
            const ticketType = ticketTypes?.find((type) => type.id === suggestion.ticketTypeId);
            if (ticketType?.automaticSeating !== 'auto') return;
            const currentSelected = seats.filter((s) => s.ticketTypeId === suggestion.ticketTypeId);
            const neededQuantity = seatNeeds[suggestion.ticketTypeId as number] || 0;
            const remaining = neededQuantity - currentSelected.length;
            if (remaining <= 0 || !suggestion.seats) return;
            const alreadyIds = new Set(currentSelected.map((s) => s.id));
            suggestion.seats
                .filter((s) => !alreadyIds.has(s.id as number))
                .slice(0, remaining)
                .forEach((seat) => {
                    newSeats.push({
                        id: seat.id as number,
                        name: seat.name as string,
                        nameI18n: (seat.nameI18n || {}) as II18nLanguageObj,
                        ticketTypeId: suggestion.ticketTypeId as number,
                        ticketTypeName: ticketType.name,
                        ticketTypeNameI18n: ticketType.nameI18n,
                    });
                });
        });
        if (newSeats.length > 0) {
            updateSeatsAndNotify([...seats, ...newSeats]);
        }
    }

    function clearAllSeats() {
        const fullAutoSeats = seats.filter((s) => fullAutoTicketTypeIds.has(s.ticketTypeId));
        updateSeatsAndNotify(fullAutoSeats);
    }

    function onSeatClick(seatId: number) {
        const hasThisSeat = seats.find((e) => e.id === seatId);
        if (hasThisSeat) {
            if (fullAutoTicketTypeIds.has(hasThisSeat.ticketTypeId)) return;
            const newSeats = seats.filter((e) => e.id !== seatId);
            setSeatsRaw(newSeats);
            postToParent({ type: MSG.SEAT_DESELECTED, seat: toExternalSeat(hasThisSeat) });
            checkAndNotifyConfirmation(newSeats);
            return;
        }

        if (seats.length >= maxSeats) return;

        const foundTicketTypes: TicketTypesSuccessResponseResponseTicketTypesItem[] = [];
        ticketTypes.forEach((ticketType) => {
            const seatsAlreadyFromType = seats.filter((e) => e.ticketTypeId === ticketType.id);
            if (
                ticketType.seats?.find((seat) => (seat.id as unknown as number) === seatId) &&
                seatNeeds[ticketType.id] > 0 &&
                seatNeeds[ticketType.id] > seatsAlreadyFromType.length
            ) {
                foundTicketTypes.push(ticketType);
            }
        });

        if (foundTicketTypes.length === 0) return;
        if (fullAutoTicketTypeIds.has(foundTicketTypes[0].id)) return;

        const newSeat = foundTicketTypes[0].seats?.find(
            (seat) => (seat.id as unknown as number) === seatId,
        );
        if (!newSeat) return;

        if (
            (filteredFreeSeats?.length ?? 0) > 0 &&
            !filteredFreeSeats?.find((seat) => Number(seat.id) === seatId)
        ) {
            return;
        }

        const addedSeat: IframeSeat = {
            id: seatId,
            name: newSeat.name,
            nameI18n: newSeat.nameI18n,
            ticketTypeId: foundTicketTypes[0].id,
            ticketTypeName: foundTicketTypes[0].name,
            ticketTypeNameI18n: foundTicketTypes[0].nameI18n,
        };
        const newSeats = [...seats, addedSeat];
        setSeatsRaw(newSeats);
        postToParent({ type: MSG.SEAT_SELECTED, seat: toExternalSeat(addedSeat) });
        checkAndNotifyConfirmation(newSeats);
    }

    // Build suggested seat IDs set for orange highlighting
    const suggestedSeatIds = new Set<number>();
    suggestedSeats.forEach((suggestion) => {
        suggestion.seats?.forEach((seat) => {
            if (seat.id != null) suggestedSeatIds.add(seat.id);
        });
    });

    const showSuggestedLegend = suggestedSeatIds.size > 0;

    const { colorMap, ticketTypeColorMap, ticketTypeNameI18nMap, dimmedSeatIds } =
        calculateColorMap(
            ticketTypes,
            allSeats,
            seatNeeds,
            seats,
            filteredFreeSeats,
            suggestedSeatIds,
        );

    if (!show) {
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                }}
            >
                Loading...
            </div>
        );
    }

    if (noSeatingRequired) {
        return (
            <div
                className="fireknife-iframe-seat-picker"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    minHeight: '120px',
                    padding: '24px',
                    textAlign: 'center',
                }}
            >
                <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
                    {t('widgetInfo.noSeatingRequired')}
                </div>
                <div style={{ fontSize: '14px', color: '#666' }}>
                    {t('widgetInfo.seatsAssignedBeforeEvent')}
                </div>
            </div>
        );
    }

    return (
        <div className="fireknife-iframe-seat-picker">
            {!panelOpen && (
                <button
                    className="fireknife-iframe-seat-picker__toggle-btn"
                    onClick={() => setPanelOpen(true)}
                >
                    ☰ {t('widgetInfo.selectYourSeats')}
                </button>
            )}
            {panelOpen && (
                <div className="fireknife-iframe-seat-picker__panel">
                    <div className="fireknife-iframe-seat-picker__panel-header">
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <h3 className="fireknife-iframe-seat-picker__panel-title">
                                {t('widgetInfo.selectYourSeats')}
                            </h3>
                            <button
                                className="fireknife-iframe-seat-picker__toggle-btn"
                                style={{
                                    position: 'static',
                                    boxShadow: 'none',
                                    border: 'none',
                                    padding: '2px 6px',
                                    fontSize: '16px',
                                }}
                                onClick={() => setPanelOpen(false)}
                            >
                                ✕
                            </button>
                        </div>
                        {(hasAutoSuggestions || hasClearableSeats) && (
                            <div className="fireknife-iframe-seat-picker__actions">
                                {hasAutoSuggestions && (
                                    <button
                                        className="fireknife-btn fireknife-btn--xs fireknife-btn--primary"
                                        onClick={acceptAllSuggestedSeats}
                                    >
                                        {t('widgetInfo.acceptSuggestedSeats')}
                                    </button>
                                )}
                                {hasClearableSeats && (
                                    <button
                                        className="fireknife-btn fireknife-btn--xs fireknife-btn--secondary"
                                        onClick={clearAllSeats}
                                    >
                                        {t('widgetInfo.clearSeats')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="fireknife-iframe-seat-picker__ticket-types">
                        {Object.entries(seatNeeds).map(([ticketTypeId, quantity]) => {
                            const ticketType = ticketTypes.find(
                                (type) => type.id === parseInt(ticketTypeId, 10),
                            );
                            if (!ticketType) return null;
                            const hasSeats = ticketType.seats && ticketType.seats.length > 0;
                            const selectedSeats = seats.filter(
                                (e) => e.ticketTypeId === parseInt(ticketTypeId, 10),
                            );
                            const isFullAuto = ticketType.automaticSeating === 'fullauto';
                            const suggestion = suggestedSeats.find(
                                (s) => s.ticketTypeId === parseInt(ticketTypeId, 10),
                            );
                            const showSuggestion =
                                suggestion &&
                                ticketType.automaticSeating === 'auto' &&
                                selectedSeats.length < quantity;

                            return (
                                <div
                                    className="fireknife-iframe-seat-picker__ticket-type"
                                    key={ticketTypeId}
                                >
                                    <div className="fireknife-iframe-seat-picker__ticket-type-header">
                                        <span className="fireknife-iframe-seat-picker__ticket-type-name">
                                            {getI18n(ticketType.name, ticketType.nameI18n)}
                                        </span>
                                        <span className="fireknife-iframe-seat-picker__ticket-type-count">
                                            {selectedSeats.length}/{quantity}
                                        </span>
                                    </div>
                                    {hasSeats && (
                                        <>
                                            {selectedSeats.length > 0 && (
                                                <div className="fireknife-iframe-seat-picker__seats">
                                                    {selectedSeats.map((seat) => (
                                                        <Chip
                                                            key={seat.id}
                                                            label={getI18n(
                                                                seat.name,
                                                                seat.nameI18n,
                                                            )}
                                                            onRemove={
                                                                !isFullAuto
                                                                    ? () => onSeatClick(seat.id)
                                                                    : undefined
                                                            }
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                            {showSuggestion && (
                                                <div className="fireknife-iframe-seat-picker__suggestions">
                                                    <div className="fireknife-iframe-seat-picker__suggestion-title">
                                                        {t('widgetInfo.suggestedSeats')}
                                                    </div>
                                                    <div className="fireknife-iframe-seat-picker__seats">
                                                        {suggestion.seats
                                                            ?.filter(
                                                                (s) =>
                                                                    !selectedSeats.some(
                                                                        (sel) =>
                                                                            sel.id ===
                                                                            (s.id as number),
                                                                    ),
                                                            )
                                                            .slice(
                                                                0,
                                                                quantity - selectedSeats.length,
                                                            )
                                                            .map((seat) => (
                                                                <span
                                                                    className="fireknife-iframe-seat-picker__suggested-seat"
                                                                    key={`suggested-${seat.id}`}
                                                                >
                                                                    {getI18n(
                                                                        seat.name,
                                                                        seat.nameI18n || {},
                                                                    )}
                                                                </span>
                                                            ))}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            <div className="fireknife-iframe-seat-picker__map">
                <SvgViewer
                    elements={displayElements}
                    colorMap={colorMap}
                    inCartSeatIds={seats.map((e) => e.id)}
                    onClickElement={onSeatClick}
                    ticketTypeColorMap={ticketTypeColorMap}
                    ticketTypeNameI18nMap={ticketTypeNameI18nMap}
                    dimmedSeatIds={dimmedSeatIds}
                    showLabel={showLabel}
                    setShowLabel={setShowLabel}
                    showSuggestedLegend={showSuggestedLegend}
                />
            </div>
        </div>
    );
}
